import { ReactElement, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface Props {
  ts: number[];
  values: number[];
  gateStart: number;
  gateEnd: number;
  bufferSec?: number;
  yLabel: string;
  unit: string;
  title: string;
  height?: number;
}

export default function TraverseTimeSeries({
  ts,
  values,
  gateStart,
  gateEnd,
  bufferSec = 5,
  yLabel,
  unit,
  title,
  height = 240,
}: Props): ReactElement {
  const data = useMemo(() => {
    if (!ts || !values || ts.length !== values.length) return [];
    const xMin = gateStart - bufferSec;
    const xMax = gateEnd + bufferSec;
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < ts.length; i++) {
      const t = ts[i];
      const v = values[i];
      if (!Number.isFinite(t) || !Number.isFinite(v)) continue;
      if (t < xMin || t > xMax) continue;
      points.push({ x: t - gateStart, y: v });
    }
    return points;
  }, [ts, values, gateStart, gateEnd, bufferSec]);

  const options: ApexOptions = useMemo(() => ({
    chart: {
      type: 'line',
      toolbar: { show: false },
      animations: { enabled: false },
      zoom: { enabled: false },
    },
    title: { text: title, align: 'left', style: { fontSize: '14px', fontWeight: 600 } },
    xaxis: {
      type: 'numeric',
      title: { text: 'Time relative to gate start (s)' },
      decimalsInFloat: 1,
    },
    yaxis: {
      title: { text: `${yLabel} (${unit})` },
      decimalsInFloat: 2,
    },
    stroke: { width: 2, curve: 'straight' },
    markers: { size: 0 },
    annotations: {
      xaxis: [
        {
          x: 0,
          x2: gateEnd - gateStart,
          fillColor: '#1e3a8a',
          opacity: 0.08,
          label: {
            text: 'Gate window',
            position: 'top',
            style: {
              color: '#1e3a8a',
              background: 'transparent',
              fontSize: '11px',
              fontWeight: 600,
            },
          },
        },
      ],
    },
    tooltip: {
      x: { formatter: (v: number) => `${v.toFixed(2)} s` },
    },
    grid: { borderColor: '#e5e7eb' },
  }), [title, yLabel, unit, gateStart, gateEnd]);

  if (data.length === 0) {
    return (
      <div className="cr-h-40 cr-flex cr-items-center cr-justify-center cr-text-gray-400 cr-text-sm cr-italic">
        No data available for {title.toLowerCase()}.
      </div>
    );
  }

  return (
    <Chart options={options} series={[{ name: yLabel, data }]} type="line" height={height} />
  );
}
